import { useEffect, useMemo, useState } from "react";
import { Zauneditor } from "../../pages/Zauneditor";
import { UndoRedo } from "../UndoRedo";
import { CameraReset } from "./CameraReset";
import { LeftRightButtons } from "./LeftRightButtons";
import { IndividualAuswahlwerteButtons } from "./IndividualAuswahlwerteButtons";
import { AutoCameraToggle } from "./AutoCameraToggle";
import { MultiselectToggle } from "./MultiselectToggle";
import { DaytimeToggle } from "./DaytimeToggle";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, [query]);

  return matches;
}

type BtnId =
  | "undoRedo"
  | "cameraReset"
  | "leftRight"
  | "individual"
  | "autoCam"
  | "multiselect"
  | "daytime";

const desktopOrder: BtnId[] = [
  "undoRedo",
  "individual",
  "autoCam",
  "cameraReset",
  "multiselect",
  "leftRight",
  "daytime",
];
const mobilePrimaryOrder: BtnId[] = ["undoRedo", "cameraReset", "leftRight"];

const mobileSecondaryOrder: BtnId[] = ["individual", "autoCam", "multiselect", "daytime"];

export function SceneButtons({ zauneditor }: { zauneditor: Zauneditor }) {
  const isCompact = useMediaQuery("(max-width: 1000px)");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!isCompact && menuOpen) setMenuOpen(false);
  }, [isCompact, menuOpen]);

  const byId: Record<BtnId, React.ReactNode> = {
    undoRedo: <UndoRedo key="undoRedo" />,
    cameraReset: <CameraReset key="cameraReset" resetFunction={zauneditor.cameraReset} />,
    leftRight: <LeftRightButtons key="leftRight" />,
    individual: <IndividualAuswahlwerteButtons key="individual" />,
    autoCam: <AutoCameraToggle key="autoCam" />,
    multiselect: <MultiselectToggle key="multiselect" />,
    daytime: <DaytimeToggle key="daytime" />,
  };

  const desktopButtons = useMemo(() => desktopOrder.map((id) => byId[id]), [byId]);
  const mobilePrimary = useMemo(() => mobilePrimaryOrder.map((id) => byId[id]), [byId]);
  const mobileSecondary = useMemo(() => mobileSecondaryOrder.map((id) => byId[id]), [byId]);
  const mid = Math.ceil(mobileSecondary.length / 2);
  const secondaryLeft = mobileSecondary.slice(0, mid);
  const secondaryRight = mobileSecondary.slice(mid);

  const buttonTitle = menuOpen
    ? I18N("aria::Weitere Optionen verbergen")
    : I18N("aria::Weitere Optionen anzeigen");

  return (
    <nav
      className="grid-area-bottom-center fade page-initial-hidden scene-buttons mobile-margin-adjust"
      data-group-key="SceneButtons"
      aria-label={I18N("aria::Szenensteuerung")}
    >
      <div className="scene-buttons__stack">
        {isCompact && (
          <div className="scene-buttons__top scene-buttons__top--grid">
            <div
              className={`scene-buttons__side scene-buttons__side--left ${menuOpen ? "open" : ""}`}
            >
              {secondaryLeft}
            </div>

            <button
              type="button"
              className="scene-buttons__toggle"
              aria-expanded={menuOpen}
              aria-label={buttonTitle}
              onClick={() => setMenuOpen((v) => !v)}
              title={buttonTitle}
            >
              {menuOpen ? <ExpandMore /> : <ExpandLess />}
            </button>

            <div
              className={`scene-buttons__side scene-buttons__side--right ${menuOpen ? "open" : ""}`}
            >
              {secondaryRight}
            </div>
          </div>
        )}

        {isCompact && <div className="scene-buttons__primary">{mobilePrimary}</div>}

        {!isCompact && <div className="scene-buttons__secondary">{desktopButtons}</div>}
      </div>
    </nav>
  );
}
